import React, { useState, useMemo } from "react";
import { Box, Chip, Button, Typography, Stack, IconButton, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";

import { HEALTH_STATUS_COLORS, SP_VISIT_COLORS, getSpVisitAge } from "../../constants/clinicalDashboard";
import { getSUDColor } from "../../constants/bhDashboard";
import BHStatusChip from "./BHStatusChip";
import TableSearchBar from "../CareManager/TableSearchBar";
import { useObservationsModal } from "../../../../context/ObservationsModalContext/ObservationsModalContext";
import { getObservationsColumn } from "../../../../components/Advanced/DataGrid/observationsColumn";

const SUD_FILTER_OPTIONS = ["All", "Drug", "Alcohol", "Opioid", "Tobacco"];

/**
 * Tabla de pacientes con Substance Use Disorders.
 * Incluye columnas de productividad (SP Visit, Obs, Tasks).
 *
 * @param {{ patients: Array, onCreateTask: (row) => void }} props
 */
export default function SUDTable({ patients = [], onCreateTask }) {
  const navigate = useNavigate();
  const { showObservationsModal } = useObservationsModal();
  const [search, setSearch] = useState("");
  const [sudFilter, setSudFilter] = useState("All");

  const filteredRows = useMemo(() => {
    const lowerSearch = search.toLowerCase();
    return patients.filter((row) => {
      if (lowerSearch && !row.name?.toLowerCase().includes(lowerSearch)) return false;
      if (sudFilter !== "All") {
        const types = row.sudTypes || [];
        const map = { Drug: "Drug Use Disorders", Alcohol: "Alcohol Use Disorders", Opioid: "Opioid Disorder Flag", Tobacco: "Tobacco Use" };
        if (!types.includes(map[sudFilter])) return false;
      }
      return true;
    });
  }, [patients, search, sudFilter]);

  const columns = [
    {
      field: "name",
      headerName: "Patient",
      flex: 1,
      minWidth: 160,
      renderCell: ({ value }) => (
        <Typography variant="body2" fontWeight={600} noWrap>{value}</Typography>
      ),
    },
    { field: "age", headerName: "Age", width: 60, align: "center", headerAlign: "center" },
    {
      field: "bhStatus",
      headerName: "BH Status",
      width: 170,
      renderCell: ({ value }) => <BHStatusChip status={value} />,
    },
    {
      field: "healthStatus",
      headerName: "Health Status",
      width: 110,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const palette = HEALTH_STATUS_COLORS[value] || HEALTH_STATUS_COLORS["No Status"];
        return (
          <Chip label={value || "N/A"} size="small" sx={{ fontWeight: 600, color: palette.text, bgcolor: palette.bg, border: `1px solid ${palette.border}` }} />
        );
      },
    },
    {
      field: "sudTypes",
      headerName: "SUD Type",
      flex: 1,
      minWidth: 180,
      renderCell: ({ value }) => {
        const types = value || [];
        return (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.3 }}>
            {types.map((t) => {
              const c = getSUDColor(t);
              const short = t.replace(" Disorders", "").replace(" Disorder Flag", "").replace(" Use", "");
              return <Chip key={t} label={short} size="small" sx={{ height: 20, fontSize: "0.62rem", fontWeight: 600, color: c.text, bgcolor: c.bg }} />;
            })}
          </Stack>
        );
      },
    },
    {
      field: "bhEREvents",
      headerName: "BH ER",
      width: 70,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => value > 0 ? (
        <Chip label={value} size="small" sx={{ fontWeight: 700, bgcolor: "#DC2626", color: "#FFF", minWidth: 28 }} />
      ) : <Box component="span" sx={{ color: "text.secondary" }}>0</Box>,
    },
    { field: "medications", headerName: "Meds", width: 60, align: "center", headerAlign: "center" },
    {
      field: "lastSantePlusVisit",
      headerName: "SP Visit",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const age = getSpVisitAge(value);
        const colors = SP_VISIT_COLORS[age];
        if (!value) return <Chip label="-" size="small" sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600, bgcolor: colors.bg, color: colors.text }} />;
        const d = new Date(value);
        const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        return <Chip label={label} size="small" sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600, bgcolor: colors.bg, color: colors.text }} />;
      },
    },
    {
      field: "observationsCount",
      headerName: "Obs",
      width: 65,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const val = value || 0;
        if (val === 0) return <Box component="span" sx={{ color: "text.secondary" }}>0</Box>;
        return <Chip label={val} size="small" sx={{ height: 20, minWidth: 24, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#DBEAFE", color: "#2563EB" }} />;
      },
    },
    {
      field: "activeTasks",
      headerName: "Tasks",
      width: 65,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const val = value || 0;
        if (val === 0) return <Box component="span" sx={{ color: "text.secondary" }}>0</Box>;
        return <Chip label={val} size="small" sx={{ height: 20, minWidth: 24, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#FEF3C7", color: "#D97706" }} />;
      },
    },
    getObservationsColumn(showObservationsModal, 4),
    {
      field: "actions",
      headerName: "Action",
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="View Patient">
            <IconButton size="small" onClick={() => navigate(`/patient-info/${row.cipId}`)} sx={{ color: "#6366F1" }}>
              <VisibilityIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AssignmentTurnedInIcon sx={{ fontSize: 16 }} />}
            onClick={() => onCreateTask?.(row)}
            sx={{ textTransform: "none", fontSize: "0.75rem" }}
          >
            Assign Intervention
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <TableSearchBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search patient..."
        filters={[
          { key: "sud", label: "SUD Type", options: SUD_FILTER_OPTIONS, value: sudFilter },
        ]}
        onFilterChange={(key, value) => {
          if (key === "sud") setSudFilter(value);
        }}
      />
      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(row) => row.cipId}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        pageSizeOptions={[5, 10, 25]}
        disableRowSelectionOnClick
        autoHeight
        sx={{
          borderRadius: 2,
          border: "none",
          "& .MuiDataGrid-cell": { fontSize: "0.78rem" },
          "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700, fontSize: "0.78rem" },
        }}
      />
    </Box>
  );
}
